import axios from 'axios';

const API = axios.create({
  baseURL: '/api',
  timeout: 120000, // 2 minuti — Llama 4 Scout può essere lento
});


// ── Interceptor autenticazione ────────────────────────────────────────────────
// Aggiunge il token a ogni richiesta (fallback se defaults non impostato)
API.interceptors.request.use(config => {
  if (!config.headers['Authorization']) {
    const token = sessionStorage.getItem('token');
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Su 401 (token scaduto/mancante) → logout forzato e ritorno al login
API.interceptors.response.use(
  r => r,
  err => {
    // 403 → permesso mancante: avviso leggibile invece del messaggio tecnico
    if (err.response?.status === 403) {
      const msg = 'Non hai il permesso per eseguire questa operazione. Se ti serve, chiedi a un amministratore di abilitarlo nel tuo ruolo.';
      err.response.data = { ...(err.response.data || {}), detail: msg };
      if (!err.config?.silenzioso) window.alert(msg);
    }
    if (err.response?.status === 401 && !err.config?.url?.includes('/login')) {
      const avevaToken = !!sessionStorage.getItem('token');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('utente');
      if (avevaToken) window.location.reload();  // solo se la sessione era attiva (token scaduto)
    }
    return Promise.reject(err);
  }
);

export const health = () => API.get('/health');
export const statistiche = () => API.get('/statistiche');
export const getUtentiAttivi = () => API.get('/utenti-attivi', { silenzioso: true });
export const documenti = (params) => API.get('/documenti', { params });
export const query = (data) => API.post('/query', data);
export const upload = (file) => {
  const form = new FormData();
  form.append('file', file);
  return API.post('/upload', form);
};

export default API;

export const fascicoli = (params) => API.get('/documenti/fascicoli', { params });
export const anteprimaDocumento = (docId) => API.get(`/documenti/${docId}/anteprima`, { responseType: 'blob' });
export const downloadDocumento = async (docId, nomeFile) => {
  try {
    const res = await API.get(`/documenti/${docId}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeFile;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Errore download documento:', err);
    alert('Errore nel download del documento');
  }
};

// ── Modulo finanziario ────────────────────────────────────────────────────────
export const tipiRicorso = () => API.get('/finanziario/tipi-ricorso');
export const getRicorsiCliente = (id) => API.get(`/finanziario/cliente/${id}/ricorsi`);
export const anteprimaCodiceRicorso = (params) => API.get('/finanziario/codice-ricorso/anteprima', { params });
export const variantiCodice = () => API.get('/finanziario/varianti-codice');
export const creaGestioneRicorso = (data) => API.post('/finanziario/gestione-ricorso', data);
export const aggiornaGestioneRicorso = (id, data) => API.patch(`/finanziario/gestione-ricorso/${id}`, data);
export const creaSentenza = (data) => API.post('/finanziario/sentenza', data);
export const aggiornaSentenza = (id, data) => API.patch(`/finanziario/sentenza/${id}`, data);
export const creditoSospeso = () => API.get('/finanziario/rendicontazione/credito-sospeso');
export const pronteOttemperanza = () => API.get('/finanziario/rendicontazione/pronte-ottemperanza');
export const cuDaRecuperare = () => API.get('/finanziario/rendicontazione/cu-da-recuperare');
export const successFeeNonPagate = () => API.get('/finanziario/rendicontazione/success-fee-non-pagate');
export const scadenzeLiquidazione = (giorni) => API.get(`/finanziario/rendicontazione/scadenze-liquidazione?giorni=${giorni}`);
export const movimentiDaVerificare = () => API.get('/finanziario/movimenti/da-verificare');
export const importaMovimenti = (data) => API.post('/finanziario/movimenti/importa', data);
export const abbinaMovimento = (id, sentenzaId) => API.patch(`/finanziario/movimenti/${id}/abbina?sentenza_id=${sentenzaId}`);

export const getRinvii = (id) => API.get(`/finanziario/rinvii/${id}`);
export const aggiungiRinvio = (data) => API.post('/finanziario/rinvio', data);

// ── Upload documenti da scheda ricorso / cliente ─────────────────────────────
// aderente (facoltativo, solo ricorsi collettivi): fascicolo personale di quel ricorrente;
// senza, sulla scheda collettiva si usa il fascicolo processuale (la destinazione la decide il server)
const paramsAderente = (aderente) => (aderente !== undefined && aderente !== null ? { aderente } : undefined);
export const uploadDocumenti = (tipo, id, files, percorsi, onUploadProgress, aderente) => {
  const form = new FormData();
  files.forEach(f => form.append('files', f));
  (percorsi || []).forEach(p => form.append('percorsi', p || ''));
  return API.post(`/upload/${tipo}/${id}`, form, { timeout: 0, onUploadProgress, params: paramsAderente(aderente) });
};
export const documentiUpload = (tipo, id, aderente) =>
  API.get(`/upload/documenti/${tipo}/${id}`, { params: paramsAderente(aderente) });
export const riprovaDocumento = (docId) => API.post(`/upload/riprova/${docId}`);

// ── Ricorsi collettivi: aderenti, atti successivi, stampa ed export ───────────
const RIC = (id) => `/finanziario/ricorso/${id}`;
export const getAderenti = (gestioneId, stato) => API.get(`${RIC(gestioneId)}/aderenti`, { params: stato ? { stato } : {} });
// ricorrenti nell'atto del ricorso (in attesa di documentazione o documentazione completa): la regola
// è solo nel backend (collettivi.STATI_NELL_ATTO); da qui si propongono appello, atti e capofila
export const getRicorrentiNellAtto = (gestioneId) => API.get(`${RIC(gestioneId)}/aderenti`, { params: { nell_atto: true } });
export const aggiungiAderente = (gestioneId, data) => API.post(`${RIC(gestioneId)}/aderenti`, data);
export const modificaAderente = (gestioneId, codiceCliente, data) => API.patch(`${RIC(gestioneId)}/aderenti/${codiceCliente}`, data);
export const eliminaAderente = (gestioneId, codiceCliente) => API.delete(`${RIC(gestioneId)}/aderenti/${codiceCliente}`);
export const spostaAderente = (gestioneId, codiceCliente, codiceDestinazione) =>
  API.post(`${RIC(gestioneId)}/aderenti/${codiceCliente}/sposta`, { codice_destinazione: codiceDestinazione });
// formato: 'stampa' (PDF) | 'export' (xlsx); stato: uno stato o 'tutti' (assente = predefinito del server)
// colonne: chiavi delle colonne in più (oltre a Cognome, Nome, Codice fiscale), separate da virgola
export const scaricaAderenti = (gestioneId, formato, stato, colonne = []) =>
  API.get(`${RIC(gestioneId)}/aderenti/${formato}`, {
    params: { ...(stato ? { stato } : {}), ...(colonne.length ? { colonne: colonne.join(',') } : {}) }, responseType: 'blob' });
export const getAtti = (gestioneId) => API.get(`${RIC(gestioneId)}/atti`);
export const getCampiPersonalizzati = (gestioneId) => API.get(`${RIC(gestioneId)}/campi-personalizzati`);
export const salvaCampiPersonalizzati = (gestioneId, campi) => API.put(`${RIC(gestioneId)}/campi-personalizzati`, { campi });
export const capofilaAutomatico = (gestioneId) => API.post(`${RIC(gestioneId)}/capofila/automatico`);
export const creaAtto = (gestioneId, data) => API.post(`${RIC(gestioneId)}/atti`, data);
export const modificaAtto = (attoId, data) => API.patch(`/finanziario/atti/${attoId}`, data);
export const eliminaAtto = (attoId) => API.delete(`/finanziario/atti/${attoId}`);
export const aggiungiAderenteAtto = (attoId, data) => API.post(`/finanziario/atti/${attoId}/aderenti`, data);
export const aggiungiTuttiAtto = (attoId) => API.post(`/finanziario/atti/${attoId}/aderenti/tutti`);
export const modificaAderenteAtto = (attoId, codiceCliente, data) => API.patch(`/finanziario/atti/${attoId}/aderenti/${codiceCliente}`, data);
export const togliAderenteAtto = (attoId, codiceCliente) => API.delete(`/finanziario/atti/${attoId}/aderenti/${codiceCliente}`);
